import React, { useEffect } from 'react';
import DocsLayout from './DocsLayout';
import { Clock, CheckCircle, AlertCircle, Lightbulb, Mail } from 'lucide-react';

const GmailSetup = () => {
  useEffect(() => {
    document.title = 'Gmail Setup Guide | TicketDesk Documentation';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Complete guide to connecting Gmail to TicketDesk. Learn how to set up Gmail OAuth, configure email forwarding, and troubleshoot common issues.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/docs/gmail-setup';
  }, []);

  return (
    <DocsLayout currentPage="/docs/gmail-setup">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <a href="/docs" className="hover:text-purple-600">Documentation</a>
          <span className="mx-2">/</span>
          <a href="/docs" className="hover:text-purple-600">Integrations</a>
          <span className="mx-2">/</span>
          <span className="text-gray-900">Gmail Setup</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          Gmail Integration Setup
        </h1>

        <p className="text-lg text-gray-600">
          Connect your Gmail account to TicketDesk to automatically convert emails into trackable support tickets.
        </p>

        <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            3 min read
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-1" />
            Last updated: January 2025
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="prose prose-lg max-w-none">
        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What You'll Need</h2>
        <ul className="space-y-2 mb-8">
          <li>A Google Workspace or Gmail account</li>
          <li>Admin access to the email account you want to connect</li>
          <li>An active TicketDesk account</li>
        </ul>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-8">
          <div className="flex">
            <Lightbulb className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Estimated Time</h4>
              <p className="text-sm text-blue-800 mb-0">
                The entire setup process takes approximately 60-90 seconds once you have your Gmail account ready.
              </p>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step-by-Step Setup</h2>

        <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 1: Navigate to Integrations</h3>
        <p className="text-gray-700 mb-4">
          From your TicketDesk dashboard, click on your workspace name in the top right, then select "Integrations" from the dropdown menu.
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
          <p className="text-sm text-gray-600 mb-2"><strong>📸 Screenshot placeholder:</strong></p>
          <div className="bg-gray-200 h-48 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">Workspace menu → Integrations screenshot</span>
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 2: Click "Connect Gmail"</h3>
        <p className="text-gray-700 mb-4">
          On the Integrations page, locate the Gmail card and click the "Connect Gmail" button.
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
          <p className="text-sm text-gray-600 mb-2"><strong>📸 Screenshot placeholder:</strong></p>
          <div className="bg-gray-200 h-48 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">Gmail integration card screenshot</span>
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 3: Authorize Google OAuth</h3>
        <p className="text-gray-700 mb-4">
          You'll be redirected to Google's secure OAuth consent screen. Follow these steps:
        </p>

        <ol className="list-decimal list-inside space-y-3 mb-6">
          <li className="text-gray-700">Select the Google account you want to connect</li>
          <li className="text-gray-700">Review the permissions TicketDesk is requesting</li>
          <li className="text-gray-700">Click "Allow" to grant access</li>
          <li className="text-gray-700">Wait to be redirected back to TicketDesk</li>
        </ol>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg my-6">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-yellow-900 mb-1">Permissions Explained</h4>
              <p className="text-sm text-yellow-800 mb-2">
                TicketDesk requests these permissions:
              </p>
              <ul className="text-sm text-yellow-800 list-disc list-inside space-y-1 mb-0">
                <li><strong>Read emails:</strong> To convert incoming emails into tickets</li>
                <li><strong>Send emails:</strong> To send replies to customers from TicketDesk</li>
                <li><strong>Modify labels:</strong> To mark emails as processed (optional)</li>
              </ul>
            </div>
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Step 4: Verify Connection</h3>
        <p className="text-gray-700 mb-4">
          Once connected, you'll see a green checkmark next to Gmail on your Integrations page. Your email address will be displayed to confirm the connection.
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
          <p className="text-sm text-gray-600 mb-2"><strong>📸 Screenshot placeholder:</strong></p>
          <div className="bg-gray-200 h-32 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">Connected Gmail status screenshot</span>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How It Works</h2>
        <p className="text-gray-700 mb-4">
          Once connected, TicketDesk automatically:
        </p>

        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-purple-700 font-bold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Monitors your inbox</h4>
                <p className="text-sm text-gray-600 mb-0">Checks for new emails every 1-2 minutes (paid plans) or every hour (free plan)</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-purple-700 font-bold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Creates tickets automatically</h4>
                <p className="text-sm text-gray-600 mb-0">Each new email thread becomes a ticket with a unique ID</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-purple-700 font-bold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Sends acknowledgment email</h4>
                <p className="text-sm text-gray-600 mb-0">Customer receives auto-reply with ticket number (configurable)</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-purple-700 font-bold text-sm">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Syncs replies</h4>
                <p className="text-sm text-gray-600 mb-0">When you reply from TicketDesk, email is sent via Gmail automatically</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Troubleshooting</h2>

        <div className="space-y-4 mb-8">
          <div className="border-l-4 border-gray-300 pl-4">
            <h4 className="font-semibold text-gray-900 mb-2">Emails aren't becoming tickets</h4>
            <p className="text-sm text-gray-700 mb-2">Common causes:</p>
            <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
              <li>Check that Gmail is showing as "Connected" on Integrations page</li>
              <li>Verify emails are arriving in the connected Gmail account</li>
              <li>Free plan: Emails are processed every 60 minutes, not instantly</li>
              <li>Check your ticket limit - you may have reached your monthly quota</li>
            </ul>
          </div>

          <div className="border-l-4 border-gray-300 pl-4">
            <h4 className="font-semibold text-gray-900 mb-2">Connection shows "Disconnected"</h4>
            <p className="text-sm text-gray-700 mb-2">
              This usually happens when you revoke Gmail access or change your Google password. Click "Reconnect" and authorize again.
            </p>
          </div>

          <div className="border-l-4 border-gray-300 pl-4">
            <h4 className="font-semibold text-gray-900 mb-2">I connected the wrong Gmail account</h4>
            <p className="text-sm text-gray-700 mb-2">
              Click "Disconnect" on the Integrations page, then reconnect with the correct account. Existing tickets won't be affected.
            </p>
          </div>

          <div className="border-l-4 border-gray-300 pl-4">
            <h4 className="font-semibold text-gray-900 mb-2">Can I connect multiple Gmail accounts?</h4>
            <p className="text-sm text-gray-700 mb-2">
              Yes, but you need separate workspaces for each Gmail account. Create additional workspaces from your account settings.
            </p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Next Steps</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <a href="/docs/ticket-management" className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
            <h3 className="font-semibold text-gray-900 mb-1">Manage Your Tickets</h3>
            <p className="text-sm text-gray-600 mb-0">Learn how to organize and respond to tickets</p>
          </a>

          <a href="/docs/facebook-setup" className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all">
            <h3 className="font-semibold text-gray-900 mb-1">Add Facebook Messenger</h3>
            <p className="text-sm text-gray-600 mb-0">Connect another channel to your unified inbox</p>
          </a>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Still Having Issues?</h3>
          <p className="text-gray-700 mb-4">
            Our support team can help you troubleshoot Gmail connection problems.
          </p>
          <a href="mailto:hello@ticketdesk.app" className="inline-flex items-center justify-center bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700">
            Contact Support
          </a>
        </div>
      </div>
    </DocsLayout>
  );
};

export default GmailSetup;
